import { Inject, Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { EgiPage } from './egi-page';
import { EGI_APP_ID_ROUTE_PARAM, EGI_APP_ROOT, NO_ROOT_ERROR_MESSAGE } from './constants';
import { EGI_PAGES_SNAPSHOT_CONFIG, EgiPagesSnapshotConfig } from './types';

@Injectable({
  providedIn: 'root'
})
export class EgiPagesSnapshot {
  private _pages?: EgiPage[];

  constructor(
    @Inject(EGI_PAGES_SNAPSHOT_CONFIG)
    private readonly config: EgiPagesSnapshotConfig,
    private readonly router: Router) {
  }

  get pageTrees(): EgiPage[] {
    if (!this._pages) {
      this.loadRouteTrees();
    }
    return this._pages;
  }

  get pages(): EgiPage[] {
    return this.pageTrees.map(p => p.asLinkedList).flat();
  }

  get homePage(): EgiPage {
    const homePage = this.pages.find(p => p.config.id === this.config.homePageId);
    if (!homePage) {
      throw new Error('The id configured as homePage, was not found with any of the EgiPages');
    }
    return homePage;
  }

  private loadRouteTrees(): void {
    const egiRoot = this.router.config.find(r => r.path === `:${EGI_APP_ID_ROUTE_PARAM}`);

    const rootLayouts = this.findRootLayoutRecursively(egiRoot);
    this.ensureRootIsCorrectlyTagged(rootLayouts);

    this._pages = rootLayouts[0].children.map(c => EgiPage.createFrom(c));
  }

  private findRootLayoutRecursively(route: Route): Routes {
    if (this.isRoot(route)) {
      return [route];
    }

    if (!route.children) {
      return [];
    }

    const rootRoutes = route.children.filter(c => this.isRoot(c));

    if (!rootRoutes.length) {
      return route.children.map(c => this.findRootLayoutRecursively(c)).flat();
    }

    return rootRoutes;
  }

  private ensureRootIsCorrectlyTagged(rootLayouts: Routes): void {
    if (!rootLayouts.length) {
      throw new Error(NO_ROOT_ERROR_MESSAGE);
    }

    if (rootLayouts.length > 1) {
      console.warn('Multiple roots detected! Make sure to only mark 1 route as root layout!');
    }
  }

  private isRoot(route: Route): boolean {
    return route?.data && route.data[EGI_APP_ROOT];
  }
}
